<div class="settings-container view">
    @if (isLoading) {
        <div class="loading">
            <mat-spinner color="accent" diameter="40"></mat-spinner>
            <p>Loading settings...</p>
        </div>
    }

    @if (error) {
        <div class="error-message">
            <p>{{ error }}</p>
        </div>
    }

    @if (propertiesError) {
        <div class="error-message">
            <p>{{ propertiesError }}</p>
        </div>
    }

    @if (!isLoading) {
        <mat-card class="cardPlain">
      <div class="custom-card-header">
        <div class="section-header">
          <app-breadcrumbs [items]="breadcrumbs"></app-breadcrumbs>
          <h1 class="m-0 f-w-500">HubSpot Settings</h1>
          <p class="m-0 help-text">Manage sync defaults and property mappings for Shine Enrich.</p>
        </div>
      </div>
      <mat-card-content class="d-flex flex-col gap-24">
          <form [formGroup]="settingsForm" class="d-flex flex-col gap-24 form-no-subscripts">
          <mat-tab-group
            class="webinar-tabs"
            animationDuration="0ms"
            [selectedIndex]="selectedIndex"
            (selectedTabChange)="onTabChange($event)"
          >
            <mat-tab>
              <ng-template mat-tab-label>
                <mat-icon>tune</mat-icon>
                <span>Configuration</span>
              </ng-template>
              <ng-template matTabContent>
                <div class="tab-content d-flex flex-col gap-16">
                  <section class="tab-section d-flex flex-col gap-12">
                    <div class="section-header">
                      <h4 class="m-0 f-s-16 f-w-600">Sync settings</h4>
                      <p class="m-0 f-s-14 text-gray-100">Control when companies sync from Enrich.</p>
                    </div>
                      @if (settingsForm.get('syncOnCompanyCreated')?.value) {
                          <mat-form-field
                                  appearance="outline"
                                  class="w-100"
                          >
                              <mat-label>Existing company sync frequency</mat-label>
                              <mat-select formControlName="existingCompanySyncFrequency">
                                  @for (option of syncFrequencyOptions; track option) {
                                      <mat-option [value]="option.value">
                                          {{ option.label }}
                                      </mat-option>
                                  }
                              </mat-select>
                              <mat-hint>How often to sync existing companies.</mat-hint>
                          </mat-form-field>
                      }
                    <mat-slide-toggle formControlName="subscriptionActive" color="primary" appearance="switch">
                      Subscription active
                    </mat-slide-toggle>
                    <mat-slide-toggle formControlName="syncOnCompanyCreated" color="primary" appearance="switch">
                      Sync on company created
                    </mat-slide-toggle>
                    <mat-slide-toggle formControlName="addCompaniesMatchingFilters" color="primary" appearance="switch">
                      Add companies matching filters
                    </mat-slide-toggle>
                  </section>
                  <section class="tab-section d-flex flex-col gap-12">
                    <div class="section-header">
                      <h4 class="m-0 f-s-16 f-w-600">Registry providers</h4>
                      <p class="m-0 f-s-14 text-gray-100">Enable one or many Nordic registries for enrich lookup, import, and company capture.</p>
                    </div>
                    <div class="registry-provider-grid">
                      @for (provider of registryProviderOptions; track provider.id) {
                        <mat-card class="registry-provider-card" [class.registry-provider-card--selected]="isProviderSelected(provider.id)">
                          <div class="registry-provider-card__top">
                            <mat-checkbox
                              [checked]="isProviderSelected(provider.id)"
                              (change)="onProviderSelectionChange(provider.id, $event.checked)"
                              color="primary"
                            >
                              {{ provider.label }}
                            </mat-checkbox>
                            <span class="registry-provider-card__country">{{ provider.country }}</span>
                          </div>
                          <p class="m-0 f-s-14 text-gray-100">{{ provider.summary }}</p>
                          <p class="m-0 f-s-13 text-gray-100">{{ provider.refreshSummary }}</p>
                        </mat-card>
                      }
                    </div>
                    <p class="m-0 f-s-13 text-gray-100">
                      The first saved provider remains the legacy default internally. BRREG stays first whenever it is enabled to preserve announcements behavior.
                    </p>

                    <div class="section-header">
                      <h4 class="m-0 f-s-16 f-w-600">Licensed data sources</h4>
                      <p class="m-0 f-s-14 text-gray-100">Control which licensed upstream datasets this client may use.</p>
                    </div>
                    <div class="registry-provider-grid">
                      @for (source of registryDataSourceOptions; track source.id) {
                        <mat-card class="registry-provider-card" [class.registry-provider-card--selected]="isDataSourceSelected(source.id)">
                          <div class="registry-provider-card__top">
                            <mat-checkbox
                              [checked]="isDataSourceSelected(source.id)"
                              [disabled]="isDataSourceSelectionDisabled(source.id)"
                              [aria-describedby]="source.id === 'dnb' ? 'dnb-connection-status' : ''"
                              (change)="onDataSourceSelectionChange(source.id, $event.checked)"
                              color="primary"
                            >
                              {{ source.label }}
                            </mat-checkbox>
                            <span class="registry-provider-card__country">
                              {{ source.id === 'dnb' ? 'Paid · tenant-owned' : 'Shared source' }}
                            </span>
                          </div>
                          <p class="m-0 f-s-14 text-gray-100">{{ source.summary }}</p>
                          <p class="m-0 f-s-13 text-gray-100">{{ source.propertySummary }}</p>
                          @if (source.id === 'dnb') {
                            <p class="m-0 f-s-13" data-testid="dnb-connection-status" id="dnb-connection-status">
                              <mat-icon aria-hidden="true">{{ dnbConnectionState === 'ready' ? 'check_circle' : 'lock' }}</mat-icon>
                              {{ dnbConnectionStatusMessage }}
                            </p>
                            <mat-form-field appearance="outline" class="w-100">
                              <mat-label>Default D&amp;B country</mat-label>
                              <mat-select formControlName="dnbCountryISOAlpha2Code" data-testid="dnb-default-country">
                                <mat-option [value]="null">No fallback country</mat-option>
                                @for (country of dnbCountryOptions; track country.value) {
                                  <mat-option [value]="country.value">{{ country.label }}</mat-option>
                                }
                              </mat-select>
                              <mat-hint>Used only when the company has no HubSpot country code.</mat-hint>
                            </mat-form-field>
                          }
                        </mat-card>
                      }
                    </div>

                    @if (isAnnouncementsSupported()) {
                      <mat-slide-toggle
                        formControlName="announcementsTimelineSyncEnabled"
                        color="primary"
                        appearance="switch"
                      >
                        Publish kunngjøringer to HubSpot timeline
                      </mat-slide-toggle>

                      @if (settingsForm.get('announcementsTimelineSyncEnabled')?.value) {
                        <p class="m-0 f-s-14 text-gray-100">
                          Backfills gradually after enabling. One timeline event per kunngjøring.
                        </p>
                      }
                    } @else {
                      <p class="m-0 f-s-13 text-gray-100">
                        Announcements timeline sync is only available for BRREG-backed Norwegian companies.
                      </p>
                    }
                  </section>
                </div>
              </ng-template>
            </mat-tab>
            <mat-tab>
              <ng-template mat-tab-label>
                <mat-icon>compare_arrows</mat-icon>
                <span>Property mapping</span>
              </ng-template>
              <ng-template matTabContent>
                <div class="tab-content d-flex flex-col gap-16">
                  <section class="tab-section d-flex flex-col gap-12">
                    <div class="section-header">
                      <h4 class="m-0 f-s-16 f-w-600">Property mapping</h4>
                      <p class="m-0 f-s-14 text-gray-100">
                        Choose where to store organization identifiers in HubSpot.
                      </p>
                    </div>
                    <mat-form-field appearance="outline" class="w-100">
                      <mat-label>Organization number property</mat-label>
                      <input
                              [matAutocomplete]="orgNumberAuto"
                              matInput
                              formControlName="crmOrgNumberProperty"
                              placeholder="Search properties..."
                              type="text"
                      />
                      <mat-autocomplete #orgNumberAuto="matAutocomplete"
                                        [displayWith]="displayHubspotProperty">
                          @for (prop of filterHubspotProperties(settingsForm.get('crmOrgNumberProperty')?.value); track prop) {
                              <mat-option [value]="prop.name">
                                  {{ prop.label }}
                              </mat-option>
                          }
                      </mat-autocomplete>
                      <mat-hint>HubSpot property to store the organization number.</mat-hint>
                        @if (settingsForm.get('crmOrgNumberProperty')?.hasError('required')) {
                            <mat-error>
                                Organization number property is required.
                            </mat-error>
                        }
                    </mat-form-field>
                    <mat-form-field appearance="outline" class="w-100">
                      <mat-label>Sub-unit number property</mat-label>
                      <input
                              [matAutocomplete]="subUnitAuto"
                              matInput
                              formControlName="crmSubUnitNumberProperty"
                              placeholder="Search properties..."
                              type="text"
                      />
                      <mat-autocomplete #subUnitAuto="matAutocomplete"
                                        [displayWith]="displayHubspotProperty">
                          @for (prop of filterHubspotProperties(settingsForm.get('crmSubUnitNumberProperty')?.value); track prop) {
                              <mat-option [value]="prop.name">
                                  {{ prop.label }}
                              </mat-option>
                          }
                      </mat-autocomplete>
                      <mat-hint>HubSpot property to store the sub-unit number.</mat-hint>
                    </mat-form-field>
                    <div class="mb-16">
                      <mat-slide-toggle formControlName="populateParentOrganizationNumberOnSubUnits">
                        Copy parent organization number to sub-units
                      </mat-slide-toggle>
                      <p class="m-0 f-s-13 text-gray-100">
                        When enabled, sub-unit syncs store the parent legal entity number in the organization number
                        property. Turning this off stops future writes but does not clear existing CRM values.
                      </p>
                    </div>
                      <div class="d-flex flex-wrap align-items-center gap-8">
                          <button
                                  (click)="runPropertySetup()"
                                  [disabled]="settingsForm.invalid || isSettingUpProperties || isSaving"
                                  class="d-inline-flex align-items-center gap-6"
                                  color="primary"
                                  mat-stroked-button
                                  type="button"
                          >
                              <mat-icon>playlist_add</mat-icon>
                              Create/refresh HubSpot properties
                          </button>
                          <span class="m-0 f-s-13 text-gray-100">
                        Adds any missing Shine Enrich fields in HubSpot.
                      </span>
                      </div>
                  </section>
                    <section class="tab-section d-flex flex-col gap-12">
                        <div class="section-header">
                            <h4 class="m-0 f-s-16 f-w-600">Overwrite policy</h4>
                            <p class="m-0 f-s-14 text-gray-100">
                                Select which mapped HubSpot properties can be replaced when Enrich has a value.
                                Unselected properties are filled only when empty.
                            </p>
                        </div>
                        @if (propertyOverwriteOptions.length > 0) {
                            <div [formGroup]="propertyOverwriteToggleGroup" class="property-overwrite-list">
                                @for (option of propertyOverwriteOptions; track option.propertyName) {
                                    <div class="property-overwrite-row">
                                        <mat-slide-toggle
                                                [formControlName]="option.propertyName"
                                                appearance="switch"
                                                color="primary"
                                        >
                                            {{ getPropertyOverwriteLabel(option) }}
                                        </mat-slide-toggle>
                                        <span class="property-overwrite-row__source">Source: {{ option.source }}</span>
                                    </div>
                                }
                            </div>
                        } @else {
                            <p class="m-0 f-s-14 text-gray-100">
                                No configurable overwrite properties are available for the current mapping.
                            </p>
                        }
                  </section>
                </div>
              </ng-template>
            </mat-tab>
              <mat-tab>
                  <ng-template mat-tab-label>
                      <mat-icon>play_circle</mat-icon>
                      <span>Operations</span>
                  </ng-template>
                  <ng-template matTabContent>
                      <div class="tab-content d-flex flex-col gap-16">
                          <section class="tab-section d-flex flex-col gap-12">
                              <div class="section-header">
                                  <h4 class="m-0 f-s-16 f-w-600">Full company sync</h4>
                                  <p class="m-0 f-s-14 text-gray-100">
                                      Start manual sync jobs and monitor their progress from sync history.
                                  </p>
                              </div>
                              <div class="operation-panel">
                                  <div class="operation-panel__body">
                                      <mat-icon class="operation-panel__icon">sync</mat-icon>
                                      <div class="d-flex flex-col gap-6">
                                          <strong>Sync all HubSpot companies</strong>
                                          <span class="m-0 f-s-14 text-gray-100">{{ syncAllImpactText }}</span>
                                      </div>
                                  </div>
                                  <div class="operation-panel__actions">
                                      <button
                                              (click)="syncNow()"
                                              [disabled]="isSyncing"
                                              class="d-inline-flex align-items-center gap-6"
                                              color="primary"
                                              mat-stroked-button
                                              type="button"
                                      >
                                          <mat-icon>sync</mat-icon>
                                          Sync all companies now
                                      </button>
                                      <button
                                              (click)="openSyncHistory()"
                                              class="d-inline-flex align-items-center gap-6"
                                              color="primary"
                                              mat-button
                                              type="button"
                                      >
                                          <mat-icon>history</mat-icon>
                                          View sync history
                                      </button>
                                  </div>
                              </div>
                          </section>

                          <section class="tab-section d-flex flex-col gap-12">
                              <app-dnb-usage-card />
                          </section>

                          <section class="tab-section d-flex flex-col gap-12">
                              <div class="section-header">
                                  <h4 class="m-0 f-s-16 f-w-600">Registry cache</h4>
                                  <p class="m-0 f-s-14 text-gray-100">
                                      Inspect the shared Nordic registry cache used by Shine Enrich.
                                  </p>
                              </div>

                              @if (registryStatusError) {
                                  <div class="error-message">
                                      <p>{{ registryStatusError }}</p>
                                  </div>
                              }

                              <div class="operation-panel">
                                  <div class="operation-panel__body">
                                      <mat-icon class="operation-panel__icon">database</mat-icon>
                                      <div class="d-flex flex-col gap-6">
                                          <strong>Managed registry data</strong>
                                          <span class="m-0 f-s-14 text-gray-100">
                                              Shine maintains registry data centrally. Tenant settings can inspect status but cannot replace or refresh the shared cache.
                                          </span>
                                      </div>
                                  </div>
                                  <div class="operation-panel__actions">
                                      <button
                                              (click)="loadRegistryStatus()"
                                              [disabled]="isLoadingRegistryStatus"
                                              class="d-inline-flex align-items-center gap-6"
                                              mat-button
                                              type="button"
                                      >
                                          <mat-icon>refresh</mat-icon>
                                          Reload status
                                      </button>
                                  </div>
                              </div>

                              <div class="registry-status-grid">
                                  @for (provider of registryProviderOptions; track provider.id) {
                                      <mat-card class="registry-status-card" [class.registry-status-card--inactive]="!isProviderSelected(provider.id)">
                                          @let status = getRegistryProviderStatus(provider.id);
                                          <div class="registry-status-card__header">
                                              <div>
                                                  <h5 class="m-0 f-s-15 f-w-600">{{ provider.label }}</h5>
                                                  <p class="m-0 f-s-13 text-gray-100">{{ isProviderSelected(provider.id) ? 'Enabled for this client' : 'Not enabled for this client' }}</p>
                                              </div>
                                          </div>

                                          @if (status) {
                                              <div class="registry-status-card__stats">
                                                  <div>
                                                      <span class="registry-status-card__label">Cached records</span>
                                                      <strong>{{ status.recordCountUnavailable ? 'Unavailable' : status.recordCount }}</strong>
                                                  </div>
                                                  <div>
                                                      <span class="registry-status-card__label">Source mode</span>
                                                      <strong>{{ status.remoteRefreshConfigured ? 'Remote' : 'Local/dev' }}</strong>
                                                  </div>
                                                  <div>
                                                      <span class="registry-status-card__label">Last source</span>
                                                      <strong>{{ status.lastSource || 'Not refreshed yet' }}</strong>
                                                  </div>
                                                  <div>
                                                      <span class="registry-status-card__label">Last refresh</span>
                                                      <strong>{{ status.lastRefreshedAt ? (status.lastRefreshedAt | date:'medium') : 'Never' }}</strong>
                                                  </div>
                                              </div>

                                              @if (status.lastRefreshResult && status.lastRefreshResult['status'] !== 'provider_status_error') {
                                                  <p class="m-0 f-s-13 text-gray-100">
                                                      Last result:
                                                      {{ status.lastRefreshResult['loadedCount'] || 0 }} loaded,
                                                      {{ status.lastRefreshResult['deletedCount'] || 0 }} deleted
                                                      @if (status.lastRefreshResult['fetchedCount']) {
                                                          , {{ status.lastRefreshResult['fetchedCount'] }} fetched
                                                      }
                                                  </p>
                                              }
                                          } @else {
                                              <p class="m-0 f-s-13 text-gray-100">
                                                  No cache status available yet for this provider.
                                              </p>
                                          }
                                      </mat-card>
                                  }
                              </div>
                          </section>

                          <section class="tab-section d-flex flex-col gap-12">
                              <div class="section-header">
                                  <h4 class="m-0 f-s-16 f-w-600">BRREG sub-unit preview</h4>
                                  <p class="m-0 f-s-14 text-gray-100">
                                      Review one bounded page of read-only sub-unit outcomes before any sync job runs.
                                  </p>
                              </div>

                              @if (!canPreviewSubUnits) {
                                  <div class="registry-cache-note" data-testid="subunit-preview-config-empty">
                                      <p class="m-0 f-s-14 text-gray-100">{{ subUnitPreviewConfigMessage }}</p>
                                  </div>
                              } @else {
                                  <div class="operation-panel">
                                      <div class="operation-panel__body">
                                          <mat-icon class="operation-panel__icon">visibility</mat-icon>
                                          <div class="d-flex flex-col gap-6">
                                              <strong>Preview BRREG sub-unit changes</strong>
                                              <span class="m-0 f-s-14 text-gray-100">{{ subUnitPreviewImpactText }}</span>
                                          </div>
                                      </div>
                                      <div class="operation-panel__actions">
                                          <button
                                                  (click)="reloadSubUnitPreview()"
                                                  [disabled]="isLoadingSubUnitPreview"
                                                  class="d-inline-flex align-items-center gap-6"
                                                  color="primary"
                                                  mat-stroked-button
                                                  type="button"
                                          >
                                              <mat-icon>refresh</mat-icon>
                                              Reload preview
                                          </button>
                                          <button
                                                  (click)="loadNextSubUnitPreviewPage()"
                                                  [disabled]="!canLoadNextSubUnitPreviewPage"
                                                  class="d-inline-flex align-items-center gap-6"
                                                  mat-button
                                                  type="button"
                                          >
                                              <mat-icon>arrow_forward</mat-icon>
                                              Next page
                                          </button>
                                      </div>
                                  </div>

                                  @if (isLoadingSubUnitPreview) {
                                      <div class="subunit-preview-loading" data-testid="subunit-preview-loading">
                                          <mat-progress-bar mode="indeterminate"></mat-progress-bar>
                                          <p class="m-0 f-s-14 text-gray-100">Loading preview rows...</p>
                                      </div>
                                  }

                                  @if (subUnitPreviewError) {
                                      <div class="error-message" data-testid="subunit-preview-error">
                                          <p>{{ subUnitPreviewError }}</p>
                                      </div>
                                  }

                                  @if (hasBackendSubUnitPreviewConfigEmpty && !subUnitPreviewError) {
                                      <div class="registry-cache-note" data-testid="subunit-preview-config-empty">
                                          <p class="m-0 f-s-14 text-gray-100">{{ subUnitPreviewConfigMessage }}</p>
                                      </div>
                                  }

                                  @if (subUnitPreview && !hasBackendSubUnitPreviewConfigEmpty && !subUnitPreviewError) {
                                      <div class="subunit-preview-summary" data-testid="subunit-preview-summary">
                                          <article class="subunit-preview-summary__card">
                                              <span>Total rows</span>
                                              <strong>{{ subUnitPreview.summary.totalRows }}</strong>
                                          </article>
                                          <article class="subunit-preview-summary__card">
                                              <span>Would create</span>
                                              <strong>{{ subUnitPreview.summary.wouldCreateRows }}</strong>
                                          </article>
                                          <article class="subunit-preview-summary__card">
                                              <span>Would update</span>
                                              <strong>{{ subUnitPreview.summary.wouldUpdateRows }}</strong>
                                          </article>
                                          <article class="subunit-preview-summary__card">
                                              <span>No-op</span>
                                              <strong>{{ subUnitPreview.summary.noOpRows }}</strong>
                                          </article>
                                          <article class="subunit-preview-summary__card">
                                              <span>Duplicates</span>
                                              <strong>{{ subUnitPreview.summary.duplicateRows }}</strong>
                                          </article>
                                          <article class="subunit-preview-summary__card">
                                              <span>Conflicts</span>
                                              <strong>{{ subUnitPreview.summary.conflictRows }}</strong>
                                          </article>
                                      </div>

                                      @if (subUnitPreview.rows.length === 0) {
                                          <div class="registry-cache-note" data-testid="subunit-preview-empty">
                                              <p class="m-0 f-s-14 text-gray-100">
                                                  No HubSpot companies with the configured sub-unit property were found on this page.
                                              </p>
                                          </div>
                                      } @else {
                                          <div class="subunit-preview-list">
                                              @for (row of subUnitPreviewRows; track row.companyId) {
                                                  <article class="subunit-preview-row" data-testid="subunit-preview-row">
                                                      <div class="subunit-preview-row__header">
                                                          <div class="subunit-preview-row__identity">
                                                              <strong>{{ row.companyName }}</strong>
                                                              <span class="m-0 f-s-13 text-gray-100">
                                                                  {{ row.companyId }} · {{ row.subUnitId }}
                                                                  @if (row.orgNumber) {
                                                                      · Org {{ row.orgNumber }}
                                                                  }
                                                              </span>
                                                          </div>
                                                          <div class="subunit-preview-row__badges">
                                                              <span class="preview-badge preview-badge--status preview-badge--{{ row.registryStatus }}">
                                                                  {{ previewRegistryStatusLabel(row) }}
                                                              </span>
                                                              @if (row.outcome) {
                                                                  <span class="preview-badge preview-badge--outcome">
                                                                      {{ row.outcome }}
                                                                  </span>
                                                              }
                                                              @for (issueCode of row.issueCodes; track issueCode) {
                                                                  <span class="preview-badge preview-badge--warning">
                                                                      {{ previewIssueLabel(issueCode) }}
                                                                  </span>
                                                              }
                                                          </div>
                                                      </div>

                                                      @if (row.registryCompanyName || row.parentOrgNumber) {
                                                          <p class="m-0 f-s-13 text-gray-100">
                                                              @if (row.registryCompanyName) {
                                                                  Registry: {{ row.registryCompanyName }}
                                                              }
                                                              @if (row.parentOrgNumber) {
                                                                  · Expected org {{ row.parentOrgNumber }}
                                                              }
                                                          </p>
                                                      }

                                                      @if (row.errorMessage) {
                                                          <p class="m-0 f-s-13 text-gray-100">{{ row.errorMessage }}</p>
                                                      } @else if (row.diffPropertyCount === 0) {
                                                          <p class="m-0 f-s-13 text-gray-100">No property differences on this page.</p>
                                                      } @else {
                                                          <div class="subunit-preview-row__diffs">
                                                              @if (row.createPropertyNames.length > 0) {
                                                                  <p class="m-0 f-s-13 text-gray-100">
                                                                      Create {{ row.createPropertyNames.length }}:
                                                                      {{ previewPropertyList(row.createPropertyNames) }}
                                                                  </p>
                                                              }
                                                              @if (row.updatePropertyNames.length > 0) {
                                                                  <p class="m-0 f-s-13 text-gray-100">
                                                                      Update {{ row.updatePropertyNames.length }}:
                                                                      {{ previewPropertyList(row.updatePropertyNames) }}
                                                                  </p>
                                                              }
                                                          </div>
                                                      }
                                                  </article>
                                              }
                                          </div>
                                      }
                                  }
                              }
                          </section>
                      </div>
                  </ng-template>
              </mat-tab>
              <mat-tab>
                  <ng-template mat-tab-label>
                      <mat-icon>fact_check</mat-icon>
                      <span>Company capture</span>
                  </ng-template>
                  <ng-template matTabContent>
                      <div class="tab-content d-flex flex-col gap-16">
                          <section class="tab-section d-flex flex-col gap-12">
                              <div class="section-header">
                                  <h4 class="m-0 f-s-16 f-w-600">Company capture (HubSpot forms)</h4>
                                  <p class="m-0 f-s-14 text-gray-100">
                                      Autocomplete Nordic company names in the form, store the organization number in a field, and on submit
                                      associate
                                      contact ↔ company in HubSpot.
                                  </p>
                              </div>
                              <mat-slide-toggle appearance="switch" color="primary"
                                                (change)="onCompanyCaptureChange($event.checked)"
                                                formControlName="companyCaptureEnabled">
                                  Enable company capture
                              </mat-slide-toggle>
                              @if (settingsForm.get('companyCaptureEnabled')?.value) {
                                  <p class="m-0 f-s-14 text-gray-100">
                                      Company capture needs contact access to associate form submissions with companies.
                                  </p>
                                  <p class="m-0 f-s-14" role="status">{{ companyCaptureAccessMessage }}</p>
                                  @if (companyCaptureAccess !== 'granted') {
                                      <button type="button" mat-stroked-button
                                              [disabled]="isAuthorizingCompanyCapture || companyCaptureAccess === 'checking' || !companyCapturePermissionConnectionId"
                                              (click)="grantCompanyCaptureContactAccess()">
                                          {{ isAuthorizingCompanyCapture ? 'Waiting for HubSpot…' : 'Grant HubSpot contact access' }}
                                      </button>
                                      <a routerLink="/enrich/setup">Select the HubSpot connection in Setup</a>
                                  }
                              }
                              <mat-form-field appearance="outline" class="w-100">
                                  <mat-label>HubSpot portal ID</mat-label>
                                  <input [value]="portalId ?? ''" disabled matInput/>
                                  <mat-hint>Used by the public autocomplete endpoint.</mat-hint>
                              </mat-form-field>
                              @if (!portalId || portalId <= 0) {
                                  <p class="m-0 f-s-14 text-gray-100">
                                      Portal ID is missing. Complete HubSpot authentication in <a
                                          routerLink="/enrich/setup">Setup</a>
                                      first.
                                  </p>
                              }
                              <div class="d-flex flex-col gap-8 company-capture-steps">
                                  <p class="m-0 f-s-14 text-gray-100">
                                      1) In HubSpot, add an orgnr field to the form (recommended hidden). Example
                                      internal name:
                                      <code>orgnr</code>.
                                  </p>
                                  <p class="m-0 f-s-14 text-gray-100">
                                      2) Paste the hosted script snippet on the HubSpot CMS page that hosts the form
                                      (never include an API key in browser code).
                                  </p>
                                  <p class="m-0 f-s-14 text-gray-100">
                                      3) Use a HubSpot workflow “Send webhook” action to call the processing endpoint
                                      with
                                      <code>x-api-key</code> (server-side), so the company is created/enriched and the
                                      contact is
                                      associated to it.
                                  </p>
                                  <p class="m-0 f-s-14 text-gray-100">
                                      4) Set <code>requireCompanySelection</code> to <code>true</code> only when a
                                      company pick is required, then customize <code>selectionRequiredMessage</code>.
                                  </p>
                              </div>
                              <div class="install-cards">
                                  <mat-card class="code-card">
                                      <div class="code-card__header">
                                          <div class="code-card__headline">
                                              <mat-card-title>Autocomplete script</mat-card-title>
                                              <mat-card-subtitle>Paste once on the page (or as a site JS file).
                                              </mat-card-subtitle>
                                          </div>
                                          <button
                                                  (click)="copySnippet(companyCaptureScriptSnippet, $event, companyCaptureScriptCopyMessage)"
                                                  aria-label="Copy company capture script"
                                                  mat-icon-button
                                                  type="button"
                                          >
                                              <mat-icon>content_copy</mat-icon>
                                          </button>
                                      </div>
                                      <pre><code>{{ companyCaptureScriptSnippet }}</code></pre>
                                  </mat-card>

                                  <mat-card class="code-card">
                                      <div class="code-card__header">
                                          <div class="code-card__headline">
                                              <mat-card-title>Workflow request (authenticated)</mat-card-title>
                                              <mat-card-subtitle>
                                                  Use in HubSpot workflow webhook. Keep the API key secret.
                                              </mat-card-subtitle>
                                          </div>
                                          <button
                                                  (click)="copySnippet(companyCaptureWorkflowSnippet, $event, companyCaptureWorkflowCopyMessage)"
                                                  aria-label="Copy workflow request"
                                                  mat-icon-button
                                                  type="button"
                                          >
                                              <mat-icon>content_copy</mat-icon>
                                          </button>
                                      </div>
                                      <pre><code>{{ companyCaptureWorkflowSnippet }}</code></pre>
                                  </mat-card>
                              </div>
                              <button
                                      (click)="openApiKeys()"
                                      class="d-inline-flex align-items-center gap-6"
                                      color="primary"
                                      mat-stroked-button
                                      type="button"
                              >
                                  <mat-icon>key</mat-icon>
                                  Open API keys
                              </button>
                          </section>
                      </div>
                  </ng-template>
              </mat-tab>
          </mat-tab-group>
              <mat-card-actions align="end" class="action-buttons p-0">
                  <button
                          (click)="saveSettings()"
                          [disabled]="settingsForm.invalid || isSaving"
                          class="d-inline-flex align-items-center gap-6"
                          color="primary"
                          mat-flat-button
                          type="button"
            >
                      <mat-icon>save</mat-icon>
                      Save settings
                  </button>
                  @if (isSetupMode) {
            <button
              mat-flat-button
              color="primary"
              (click)="saveSettingsAndContinueSetup()"
              [disabled]="settingsForm.invalid || isSaving"
              type="button"
              class="d-inline-flex align-items-center gap-6"
            >
              <mat-icon>arrow_forward</mat-icon>
              Save and continue setup
            </button>
                  }
                  <button
                          (click)="loadSettings()"
                          [disabled]="isLoading || isSaving"
                          class="d-inline-flex align-items-center gap-6"
                          color="primary"
                          mat-stroked-button
                          type="button"
            >
                      <mat-icon>refresh</mat-icon>
                      Reset
                  </button>
              </mat-card-actions>
          </form>
      </mat-card-content>
        </mat-card>
    }
</div>
